import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { colorTokens, getSpacing, getTypography } from "../../../designTokens";
import searchIcon from "../../../Assets/icons/Search.svg";
import arrowIcon from "../../../Assets/icons/dropDownIcon.svg";

export interface SingleSelectOption {
  label: string;
  value: string;
  /** Optional right-aligned secondary text (e.g. UHID or status). */
  rightLabel?: string;
}

export interface SingleSelectOptionGroup {
  label: string;
  options: SingleSelectOption[];
}

export interface SingleSelectDropdownProps {
  options: SingleSelectOption[];
  /** When set, options are rendered under non-selectable group headers. */
  optionGroups?: SingleSelectOptionGroup[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  idPrefix?: string;
  minWidth?: string;
  maxHeight?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  /** Label shown at top-left when focused or has value */
  label?: string;
  /** Optional suffix for placeholder (e.g. red asterisk for required) */
  placeholderSuffix?: React.ReactNode;
  /** Optional id for the trigger button (for testing) */
  id?: string;
  /** When true, sets aria-busy on the trigger (e.g. when options are loading) */
  ariaBusy?: boolean;
  /** "filter" = compact boxed trigger, "filter-transparent" = compact text-like filter trigger. Default = standard form field look. */
  triggerVariant?: "default" | "filter" | "filter-transparent";
  /** Border radius for trigger, open search box, and dropdown panel (embed / form preview). Default = token 8px. */
  triggerBorderRadius?: string;
  /** `floating` = label on top border; `stacked` = label above field (standard form). */
  labelLayout?: "floating" | "stacked";
  /** Optional callback for API-driven search. When provided, parent controls option filtering. */
  onSearchChange?: (query: string) => void;
  /** When true, a clear (X) control appears if a value is selected. */
  clearable?: boolean;
}

const SingleSelectDropdown: React.FC<SingleSelectDropdownProps> = ({
  options,
  optionGroups,
  value,
  onChange,
  placeholder = "Select option",
  idPrefix = "single-select",
  minWidth = "280px",
  maxHeight = "400px",
  disabled = false,
  error = false,
  helperText,
  required = false,
  label,
  placeholderSuffix,
  id: triggerId,
  ariaBusy = false,
  triggerVariant = "default",
  triggerBorderRadius = getSpacing("8px"),
  labelLayout = "floating",
  onSearchChange,
  clearable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const flatOptions = useMemo(
    () =>
      optionGroups && optionGroups.length > 0
        ? optionGroups.flatMap((group) => group.options)
        : (options ?? []),
    [optionGroups, options]
  );

  // Filter options based on search query (memoized to avoid spurious effect runs that reset list scroll)
  const filteredOptions = useMemo(
    () =>
      onSearchChange
        ? flatOptions.filter(
            (option) => Boolean(option) && typeof option.value === "string"
          )
        : flatOptions.filter((option) => {
            if (!option || typeof option.value !== "string") return false;
            const optionLabel =
              typeof option.label === "string" ? option.label : "";
            const query = typeof searchQuery === "string" ? searchQuery : "";
            return optionLabel.toLowerCase().includes(query.toLowerCase());
          }),
    [flatOptions, searchQuery, onSearchChange]
  );

  const filteredOptionGroups = useMemo(() => {
    if (!optionGroups || optionGroups.length === 0) return undefined;
    const allowed = new Set(filteredOptions.map((option) => option.value));
    return optionGroups
      .map((group) => ({
        ...group,
        options: group.options.filter((option) => allowed.has(option.value)),
      }))
      .filter((group) => group.options.length > 0);
  }, [optionGroups, filteredOptions]);

  const minWidthNum =
    typeof minWidth === "string" && minWidth.endsWith("px")
      ? parseInt(minWidth, 10) || 280
      : 280;

  const maxHeightNum =
    typeof maxHeight === "string" && maxHeight.endsWith("px")
      ? parseInt(maxHeight, 10) || 400
      : 400;

  const computeDropdownPosition = () => {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();

    const margin = 8;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || 0;
    const spaceBelow = viewportHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;

    // Prefer opening below; if not enough space and more room above, open above.
    const shouldOpenAbove =
      spaceBelow < Math.min(maxHeightNum, 240) && spaceAbove > spaceBelow;

    // Measure actual dropdown height when possible so that when opening above,
    // the bottom edge sits just above the trigger with a constant gap.
    const menuRect = dropdownMenuRef.current?.getBoundingClientRect();
    const menuHeight = menuRect?.height ?? maxHeightNum;

    const top = shouldOpenAbove
      ? Math.max(rect.top - menuHeight - margin, margin)
      : rect.bottom + margin;

    return {
      top,
      left: rect.left,
      width: Math.max(rect.width, minWidthNum),
    };
  };

  // Position dropdown (for portal) - width matches trigger exactly; opens above if not enough space below
  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownPosition(null);
      return;
    }
    const pos = computeDropdownPosition();
    setDropdownPosition(pos);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- computeDropdownPosition closes over refs; minWidth/maxHeight changes already covered
  }, [isOpen, minWidthNum, maxHeightNum]);

  // Keep dropdown position in sync when page or any scroll container scrolls or window resizes
  useEffect(() => {
    if (!isOpen) return;
    let rafId: number;
    const updatePosition = () => {
      const pos = computeDropdownPosition();
      if (pos) {
        setDropdownPosition(pos);
      }
      rafId = requestAnimationFrame(updatePosition);
    };
    rafId = requestAnimationFrame(updatePosition);
    const onResize = () => {
      const pos = computeDropdownPosition();
      if (pos) {
        setDropdownPosition(pos);
      }
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- computeDropdownPosition closes over refs; minWidth/maxHeight changes already covered
  }, [isOpen, minWidthNum, maxHeightNum]);

  // Focus input and reset active index when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      onSearchChange?.("");
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen, onSearchChange]);

  // Reset active index when filtered options change
  useEffect(() => {
    if (isOpen && filteredOptions.length > 0) {
      const selectedIdx = filteredOptions.findIndex(
        (opt) => opt.value === value
      );
      setActiveIndex(selectedIdx >= 0 ? selectedIdx : 0);
    }
  }, [isOpen, filteredOptions, value]);

  // Scroll active option into view when activeIndex changes
  useEffect(() => {
    if (isOpen && filteredOptions.length > 0) {
      const el = optionRefs.current.get(activeIndex);
      el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeIndex, isOpen, filteredOptions.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = triggerRef.current?.contains(target);
      const inMenu = dropdownMenuRef.current?.contains(target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = flatOptions.find((opt) => opt?.value === value);
  const hasRightLabel = selectedOption?.rightLabel != null;
  const displayLabel = selectedOption?.label ?? "";
  const displayRightLabel = selectedOption?.rightLabel ?? "";
  const shouldShowLabel = label && (isOpen || !!value);
  const isCompactFilterTrigger =
    triggerVariant === "filter" || triggerVariant === "filter-transparent";
  const isTransparentFilterTrigger = triggerVariant === "filter-transparent";
  const showClearButton = Boolean(clearable && value && !disabled);
  const closedTriggerPaddingRight = isCompactFilterTrigger
    ? showClearButton
      ? getSpacing("48px")
      : getSpacing("32px")
    : showClearButton
      ? getSpacing("56px")
      : getSpacing("40px");

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setTimeout(() => setIsOpen(false), 0);
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    onChange("");
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      return;
    }
    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : 0
      );
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : filteredOptions.length - 1
      );
      return;
    }
    if (e.key === "Enter" && filteredOptions.length > 0) {
      e.preventDefault();
      handleSelect(filteredOptions[activeIndex].value);
      return;
    }
  };

  const floatingLabelStyle: React.CSSProperties = {
    ...getTypography("Body/Medium"),
    color: colorTokens["Text/Grey"],
    position: "absolute",
    top: "-10px",
    left: getSpacing("12px"),
    backgroundColor: colorTokens["Background/White"],
    padding: `0 ${getSpacing("4px")}`,
    zIndex: 1,
    display: shouldShowLabel ? "block" : "none",
  };

  const stackedLabelStyle: React.CSSProperties = {
    ...getTypography("Body/Medium"),
    color: colorTokens["Text/Black"],
    display: "block",
    marginBottom: getSpacing("8px"),
  };

  return (
    <div style={{ position: "relative", width: "100%", minWidth: 0 }}>
      {label && (
        <label
          htmlFor={triggerId ?? `${idPrefix}-trigger`}
          style={
            labelLayout === "stacked" ? stackedLabelStyle : floatingLabelStyle
          }
        >
          {label}
          {required && (
            <span
              style={{
                color: colorTokens["Alert/Red/Dark"],
                marginLeft: getSpacing("2px"),
              }}
            >
              *
            </span>
          )}
        </label>
      )}

      {/* Trigger / Search box - same box serves as both */}
      <div
        ref={triggerRef}
        style={{ position: "relative", width: "100%", minWidth: 0 }}
      >
        {isOpen ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: getSpacing("8px"),
              padding: isCompactFilterTrigger
                ? `${getSpacing("8px")} ${getSpacing("32px")} ${getSpacing("8px")} ${getSpacing("12px")}`
                : `${getSpacing("12px")} ${getSpacing("40px")} ${getSpacing("12px")} ${getSpacing("16px")}`,
              borderRadius: triggerBorderRadius,
              border: `1px solid ${error ? colorTokens["Alert/Red/Dark"] : colorTokens["Primary/Default"]}`,
              backgroundColor:
                triggerVariant === "filter"
                  ? colorTokens["Background/Greyish"]
                  : colorTokens["Background/White"],
              minHeight: isCompactFilterTrigger ? undefined : "48px",
              boxSizing: "border-box",
            }}
          >
            <img
              src={searchIcon}
              alt="Search"
              style={{
                width: "14px",
                height: "14px",
                opacity: 0.6,
                flexShrink: 0,
              }}
            />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                onSearchChange?.(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                ...getTypography("Body/Medium"),
                fontSize: "14px",
                color: colorTokens["Text/Black"],
                backgroundColor: "transparent",
              }}
              id={`${idPrefix}-search-input`}
              aria-label="Search options"
              aria-controls={`${idPrefix}-dropdown`}
              aria-activedescendant={
                filteredOptions.length > 0
                  ? `${idPrefix}-option-${activeIndex}`
                  : undefined
              }
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={handleTriggerClick}
            disabled={disabled}
            style={{
              width: "100%",
              padding: isCompactFilterTrigger
                ? `${getSpacing("8px")} ${closedTriggerPaddingRight} ${getSpacing("8px")} ${getSpacing("12px")}`
                : `${getSpacing("12px")} ${closedTriggerPaddingRight} ${getSpacing("12px")} ${getSpacing("16px")}`,
              borderRadius: triggerBorderRadius,
              border: isTransparentFilterTrigger
                ? "none"
                : `1px solid ${error ? colorTokens["Alert/Red/Dark"] : colorTokens["Borders/Light"]}`,
              backgroundColor:
                triggerVariant === "filter"
                  ? colorTokens["Background/Greyish"]
                  : isTransparentFilterTrigger
                    ? "transparent"
                    : disabled
                      ? colorTokens["Background/Greyish"]
                      : colorTokens["Background/White"],
              ...getTypography("Body/Medium"),
              color:
                isTransparentFilterTrigger || value
                  ? colorTokens["Text/Black"]
                  : colorTokens["Text/Light Grey"],
              cursor: disabled ? "not-allowed" : "pointer",
              textAlign: "left",
              boxSizing: "border-box",
              minHeight: isCompactFilterTrigger ? undefined : "48px",
              display: "flex",
              alignItems: "center",
            }}
            id={triggerId ?? `${idPrefix}-trigger`}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-label={label || placeholder}
            aria-busy={ariaBusy}
          >
            {value && hasRightLabel ? (
              <>
                <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  {displayLabel}
                </span>
                <span
                  style={{
                    flexShrink: 0,
                    marginLeft: "auto",
                    ...getTypography("Body/Medium"),
                    color: colorTokens["Text/Light Grey"],
                  }}
                >
                  {displayRightLabel}
                </span>
              </>
            ) : (
              <span style={{ flex: 1, textAlign: "left" }}>
                {displayLabel || (
                  <>
                    {placeholder}
                    {placeholderSuffix}
                  </>
                )}
              </span>
            )}
          </button>
        )}
        {showClearButton && !isOpen && (
          <button
            type="button"
            onClick={handleClear}
            onMouseDown={(event) => event.preventDefault()}
            aria-label={`Clear ${label || placeholder}`}
            id={`${idPrefix}-clear-button`}
            style={{
              position: "absolute",
              right: getSpacing("32px"),
              top: "50%",
              transform: "translateY(-50%)",
              width: getSpacing("16px"),
              height: getSpacing("16px"),
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M9 3L3 9M3 3L9 9"
                stroke={colorTokens["Text/Grey"]}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        {/* Arrow icon - absolutely positioned to align with other form selects */}
        <img
          src={arrowIcon}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            right: getSpacing("12px"),
            top: "50%",
            transform: "translateY(-50%)",
            width: "16px",
            height: "16px",
            pointerEvents: "none",
            zIndex: 1,
            opacity: 0.6,
          }}
        />
      </div>

      {helperText && (
        <p
          id={`${idPrefix}-helper-text`}
          role={error ? "alert" : undefined}
          style={{
            marginTop: getSpacing("4px"),
            marginLeft: getSpacing("16px"),
            marginRight: getSpacing("16px"),
            ...getTypography("Body/Small"),
            color: error
              ? colorTokens["Alert/Red/Dark"]
              : colorTokens["Text/Light Grey"],
          }}
        >
          {helperText}
        </p>
      )}

      {/* Dropdown Menu - portaled */}
      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownMenuRef}
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              minWidth: dropdownPosition.width,
              maxWidth: dropdownPosition.width,
              maxHeight: maxHeight,
              backgroundColor: colorTokens["Background/White"],
              border: `1px solid ${colorTokens["Borders/Light"]}`,
              borderRadius: triggerBorderRadius,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              zIndex: 11000,
              pointerEvents: "auto",
              boxSizing: "border-box",
            }}
            id={`${idPrefix}-dropdown`}
            role="listbox"
          >
            {/* Options List */}
            <div
              style={{
                padding: getSpacing("8px"),
                overflowY: "auto",
                overflowX: "hidden",
                maxHeight: "300px",
                minWidth: 0,
              }}
              className="custom-scrollbar"
            >
              {filteredOptionGroups
                ? filteredOptionGroups.map((group) => {
                    const groupIdSuffix = group.label
                      .replace(/\s+/g, "-")
                      .toLowerCase();
                    return (
                      <div key={group.label}>
                        <div
                          id={`${idPrefix}-group-${groupIdSuffix}`}
                          role="presentation"
                          style={{
                            padding: getSpacing("8px"),
                            marginBottom: getSpacing("4px"),
                            fontSize: "12px",
                            fontWeight: 600,
                            lineHeight: 1.4,
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                            color: colorTokens["Text/Grey"],
                            fontFamily: getTypography("Body/Small").fontFamily,
                          }}
                        >
                          {group.label}
                        </div>
                        {group.options.map((option) => {
                          const index = filteredOptions.findIndex(
                            (item) => item.value === option.value
                          );
                          const isSelected = value === option.value;
                          const isActive = index === activeIndex;
                          return (
                            <div
                              key={option.value}
                              ref={(el) => {
                                if (el && index >= 0) {
                                  optionRefs.current.set(index, el);
                                }
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleSelect(option.value)}
                              onMouseEnter={(e) => {
                                if (index >= 0) setActiveIndex(index);
                                e.currentTarget.style.backgroundColor =
                                  colorTokens["Background/Greyish"];
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                              }}
                              role="option"
                              aria-selected={isSelected}
                              tabIndex={-1}
                              id={`${idPrefix}-option-${index}`}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: getSpacing("8px"),
                                padding: getSpacing("8px"),
                                marginBottom: getSpacing("4px"),
                                cursor: "pointer",
                                borderRadius: getSpacing("4px"),
                                transition: "background-color 0.2s",
                                backgroundColor: isActive
                                  ? colorTokens["Background/Greyish"]
                                  : "transparent",
                              }}
                            >
                              <span
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  ...getTypography("Body/Medium"),
                                  color: colorTokens["Text/Black"],
                                  cursor: "pointer",
                                }}
                              >
                                {option.label}
                              </span>
                              {option.rightLabel != null && (
                                <span
                                  style={{
                                    flexShrink: 0,
                                    marginLeft: "auto",
                                    ...getTypography("Body/Medium"),
                                    color: colorTokens["Text/Light Grey"],
                                    cursor: "pointer",
                                  }}
                                >
                                  {option.rightLabel}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })
                : filteredOptions.map((option, index) => {
                    const isSelected = value === option.value;
                    const isActive = index === activeIndex;
                    return (
                      <div
                        key={option.value}
                        ref={(el) => {
                          if (el) optionRefs.current.set(index, el);
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSelect(option.value)}
                        onMouseEnter={(e) => {
                          setActiveIndex(index);
                          e.currentTarget.style.backgroundColor =
                            colorTokens["Background/Greyish"];
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                        role="option"
                        aria-selected={isSelected}
                        tabIndex={-1}
                        id={`${idPrefix}-option-${index}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: getSpacing("8px"),
                          padding: getSpacing("8px"),
                          marginBottom: getSpacing("4px"),
                          cursor: "pointer",
                          borderRadius: getSpacing("4px"),
                          transition: "background-color 0.2s",
                          backgroundColor: isActive
                            ? colorTokens["Background/Greyish"]
                            : "transparent",
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            ...getTypography("Body/Medium"),
                            color: colorTokens["Text/Black"],
                            cursor: "pointer",
                          }}
                        >
                          {option.label}
                        </span>
                        {option.rightLabel != null && (
                          <span
                            style={{
                              flexShrink: 0,
                              marginLeft: "auto",
                              ...getTypography("Body/Medium"),
                              color: colorTokens["Text/Light Grey"],
                              cursor: "pointer",
                            }}
                          >
                            {option.rightLabel}
                          </span>
                        )}
                      </div>
                    );
                  })}

              {filteredOptions.length === 0 && (
                <div
                  style={{
                    ...getTypography("Body/Medium"),
                    color: colorTokens["Text/Light Grey"],
                    textAlign: "center",
                    padding: getSpacing("16px"),
                  }}
                >
                  No options found
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SingleSelectDropdown;
